import { PeriodState } from "../../TransactionsPage/types";
import { PERIOD_MONTH, PERIOD_YEAR } from "../TransactionsFilters/constants";
import { INSIGHTS_MONTH_WINDOW } from "./constants";
import { InsightsBucketSlot, InsightsWindow } from "./types";

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const pad2 = (value: number): string => String(value).padStart(2, "0");
const monthKey = (year: number, month: number): string =>
  `${year}-${pad2(month)}`;
const firstDayOfMonth = (year: number, month: number): string =>
  `${monthKey(year, month)}-01`;
const lastDayOfMonth = (year: number, month: number): string => {
  const day = new Date(year, month, 0).getDate();
  return `${monthKey(year, month)}-${pad2(day)}`;
};

const buildMonthlyWindow = (period: PeriodState): InsightsWindow => {
  const slots: InsightsBucketSlot[] = [];
  for (let offset = INSIGHTS_MONTH_WINDOW - 1; offset >= 0; offset -= 1) {
    const date = new Date(period.year, period.month - 1 - offset, 1);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    slots.push({
      key: monthKey(year, month),
      label: SHORT_MONTHS[month - 1],
      inSelection: year === period.year && month === period.month,
    });
  }
  const startDate = new Date(
    period.year,
    period.month - INSIGHTS_MONTH_WINDOW,
    1,
  );
  const from = firstDayOfMonth(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
  );
  const to = lastDayOfMonth(period.year, period.month);
  const startLabel = `${SHORT_MONTHS[startDate.getMonth()]} ${startDate.getFullYear()}`;
  const endLabel = `${SHORT_MONTHS[period.month - 1]} ${period.year}`;
  const selectionLabel = `${SHORT_MONTHS[period.month - 1]}`;
  return {
    bucket: "month",
    from,
    to,
    slots,
    cashflowSubtitle: `monthly · ${startLabel} – ${endLabel} · selection: ${selectionLabel}`,
    categoriesSubtitle: `${SHORT_MONTHS[period.month - 1]} ${period.year}`,
  };
};

const monthRangeWindow = (
  endYear: number,
  endMonth: number,
  count: number,
  subtitlePrefix: string,
  categoriesSubtitle: string,
): InsightsWindow => {
  const slots: InsightsBucketSlot[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const date = new Date(endYear, endMonth - 1 - offset, 1);
    slots.push({
      key: monthKey(date.getFullYear(), date.getMonth() + 1),
      label: SHORT_MONTHS[date.getMonth()],
      inSelection: true,
    });
  }
  const start = new Date(endYear, endMonth - count, 1);
  const startLabel = `${SHORT_MONTHS[start.getMonth()]} ${start.getFullYear()}`;
  const endLabel = `${SHORT_MONTHS[endMonth - 1]} ${endYear}`;
  return {
    bucket: "month",
    from: firstDayOfMonth(start.getFullYear(), start.getMonth() + 1),
    to: lastDayOfMonth(endYear, endMonth),
    slots,
    cashflowSubtitle: `monthly · ${startLabel} – ${endLabel} · selection: ${subtitlePrefix}`,
    categoriesSubtitle,
  };
};

const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const dailyWindow = (days: number): InsightsWindow => {
  const slots: InsightsBucketSlot[] = [];
  const today = new Date();
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - offset,
    );
    slots.push({
      key: isoDate(date),
      label: `${date.getDate()}`,
      inSelection: true,
    });
  }
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (days - 1),
  );
  return {
    bucket: "day",
    from: isoDate(start),
    to: isoDate(today),
    slots,
    cashflowSubtitle: `daily · last ${days} days · selection: last ${days} days`,
    categoriesSubtitle: `last ${days} days`,
  };
};

const weeklyWindow = (days: number): InsightsWindow => {
  const today = new Date();
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (days - 1),
  );
  const slots: InsightsBucketSlot[] = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const isoYear = cursor.getFullYear();
    const onejan = new Date(isoYear, 0, 1);
    const week = Math.ceil(
      ((cursor.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) /
        7,
    );
    const key = `${isoYear}-W${pad2(week)}`;
    if (!slots.some((slot) => slot.key === key)) {
      slots.push({ key, label: `W${week}`, inSelection: true });
    }
    cursor.setDate(cursor.getDate() + 7);
  }
  return {
    bucket: "week",
    from: isoDate(start),
    to: isoDate(today),
    slots,
    cashflowSubtitle: `weekly · last ${days} days · selection: last ${days} days`,
    categoriesSubtitle: `last ${days} days`,
  };
};

const allTimeWindow = (): InsightsWindow => {
  const today = new Date();
  return {
    bucket: "year",
    from: "1970-01-01",
    to: isoDate(today),
    slots: [],
    cashflowSubtitle: `yearly · all time · selection: all time`,
    categoriesSubtitle: `all time`,
  };
};

export const getInsightsWindow = (
  period: PeriodState,
  isCompact: boolean,
): InsightsWindow => {
  if (period.dateRange === PERIOD_MONTH) {
    return buildMonthlyWindow(period);
  }
  if (period.dateRange === PERIOD_YEAR) {
    return monthRangeWindow(
      period.year,
      12,
      12,
      String(period.year),
      String(period.year),
    );
  }
  if (period.dateRange === "6m") {
    return monthRangeWindow(
      period.year,
      period.month,
      6,
      "last 6 months",
      "last 6 months",
    );
  }
  if (period.dateRange === "30d") {
    return weeklyWindow(30);
  }
  if (period.dateRange === "7d") {
    return dailyWindow(7);
  }
  return allTimeWindow();
};
